import { useCallback, useEffect, useState } from "react";
import {
    approveAdminAdvertisement,
    createAdminAdvertisement,
    deleteAdminAdvertisement,
    hideAdminAdvertisement,
    listAdminAdvertisements,
    publishAdminAdvertisement,
    updateAdminAdvertisement,
    uploadFile
} from "@/api/admin.api.js";
import { useAuth } from "@/auth/AuthContext.jsx";
import ConfirmDialog from "@/components/ConfirmDialog.jsx";
import RowActionsMenu from "@/components/RowActionsMenu.jsx";
import BackLink from "@/components/BackLink.jsx";
import ProductImageGallery from "@/components/ProductImageGallery.jsx";
import ImageUploadSaveHint from "@/components/ImageUploadSaveHint.jsx";
import {
    advertisementStatusClassName,
    formatAdvertisementStatus
} from "@/utils/format.js";
import {
    canApproveAdvertisement,
    canMutateAdvertisement,
    canPublishAdvertisementLive
} from "@/utils/staffAccess.js";
import "./admin-advertisements.css";

const emptyForm = {
    code: "",
    url: "",
    title: "",
    description: "",
    subtitle: "",
    insertAfter: 6,
    intervalSeconds: 5,
    imageFileIds: []
};

function msToSeconds(intervalMs) {
    const ms = Number(intervalMs);
    if (!Number.isFinite(ms) || ms <= 0) {
        return 5;
    }
    return Math.max(1, Math.round(ms / 1000));
}

function secondsToMs(intervalSeconds) {
    const seconds = Number(intervalSeconds);
    if (!Number.isFinite(seconds) || seconds <= 0) {
        return 5000;
    }
    return Math.max(1000, Math.round(seconds) * 1000);
}

function toForm(ad) {
    return {
        code: ad.code || "",
        url: ad.url || "",
        title: ad.title || "",
        description: ad.description || "",
        subtitle: ad.subtitle || "",
        insertAfter: ad.insertAfter ?? 6,
        intervalSeconds: msToSeconds(ad.intervalMs),
        imageFileIds: ad.imageFileIds ? [...ad.imageFileIds] : []
    };
}

export default function AdminAdvertisementsSection({ setMessage, setError }) {
    const { role } = useAuth();
    const isAdminPublisher = canPublishAdvertisementLive(role);
    const canApprove = canApproveAdvertisement(role);
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState("list");
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [pendingImageSave, setPendingImageSave] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await listAdminAdvertisements();
            setAds(response.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [setError]);

    useEffect(() => {
        load();
    }, [load]);

    const openCreate = () => {
        setSelected(null);
        setForm(emptyForm);
        setPendingImageSave(false);
        setViewMode("form");
        setMessage("");
        setError("");
    };

    const openDetail = (ad) => {
        setSelected(ad);
        setForm(toForm(ad));
        setPendingImageSave(false);
        setViewMode("detail");
        setMessage("");
        setError("");
    };

    const openEdit = (ad = selected) => {
        if (!ad || !canMutateAdvertisement(role, ad.status)) {
            return;
        }
        setSelected(ad);
        setForm(toForm(ad));
        setPendingImageSave(false);
        setViewMode("form");
        setMessage("");
        setError("");
    };

    const backToList = async () => {
        setViewMode("list");
        setSelected(null);
        setForm(emptyForm);
        setPendingImageSave(false);
        await load();
    };

    const payloadFromForm = () => ({
        code: form.code.trim(),
        url: form.url.trim(),
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        subtitle: form.subtitle.trim() || undefined,
        insertAfter: Number(form.insertAfter) || 0,
        intervalMs: secondsToMs(form.intervalSeconds),
        imageFileIds: form.imageFileIds
    });

    const handleSave = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError("");
        try {
            const payload = payloadFromForm();
            const response = selected
                ? await updateAdminAdvertisement(selected.id, payload)
                : await createAdminAdvertisement(payload);
            setMessage(selected ? "Publicité mise à jour." : "Publicité créée (masquée).");
            setSelected(response.data);
            setForm(toForm(response.data));
            setPendingImageSave(false);
            setViewMode("detail");
            await load();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handlePublish = async (ad = selected) => {
        if (!ad) {
            return;
        }
        try {
            const response = await publishAdminAdvertisement(ad.id);
            setMessage(
                isAdminPublisher
                    ? "Publicité publiée sur le catalogue."
                    : "Demande envoyée — la publicité est en attente d'approbation et n'est pas encore en ligne."
            );
            if (selected?.id === ad.id || viewMode === "detail") {
                setSelected(response.data);
            }
            await load();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleApprove = async (ad = selected) => {
        if (!ad || !canApprove) {
            return;
        }
        try {
            const response = await approveAdminAdvertisement(ad.id);
            setMessage("Publicité approuvée et publiée sur le catalogue.");
            if (selected?.id === ad.id || viewMode === "detail") {
                setSelected(response.data);
            }
            await load();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleHide = async (ad = selected) => {
        if (!ad) {
            return;
        }
        try {
            const response = await hideAdminAdvertisement(ad.id);
            setMessage(
                ad.status === "PENDING_APPROVAL"
                    ? "Demande refusée — la publicité est masquée."
                    : "Publicité masquée."
            );
            if (selected?.id === ad.id || viewMode === "detail") {
                setSelected(response.data);
            }
            await load();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async () => {
        if (!pendingDelete) {
            return;
        }
        try {
            await deleteAdminAdvertisement(pendingDelete.id);
            setMessage("Publicité supprimée.");
            setPendingDelete(null);
            if (selected?.id === pendingDelete.id) {
                await backToList();
            } else {
                await load();
            }
        } catch (err) {
            setError(err.message);
            setPendingDelete(null);
        }
    };

    const onUploadImages = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = "";
        if (files.length === 0) {
            return;
        }
        setError("");
        try {
            const uploaded = [];
            for (const file of files) {
                const data = await uploadFile(file);
                if (data?.fileId) {
                    uploaded.push(data.fileId);
                }
            }
            setForm((current) => ({
                ...current,
                imageFileIds: [...current.imageFileIds, ...uploaded]
            }));
            setPendingImageSave(true);
        } catch (err) {
            setError(err.message);
        }
    };

    const removeImage = (fileId) => {
        setForm((current) => ({
            ...current,
            imageFileIds: current.imageFileIds.filter((id) => id !== fileId)
        }));
    };

    const moveImage = (fileId, direction) => {
        setForm((current) => {
            const index = current.imageFileIds.indexOf(fileId);
            const nextIndex = index + direction;
            if (index < 0 || nextIndex < 0 || nextIndex >= current.imageFileIds.length) {
                return current;
            }
            const imageFileIds = [...current.imageFileIds];
            [imageFileIds[index], imageFileIds[nextIndex]] = [imageFileIds[nextIndex], imageFileIds[index]];
            return { ...current, imageFileIds };
        });
    };

    const buildAdvertMenuItems = (ad, { includeView = true } = {}) => {
        if (!ad) {
            return [];
        }
        const items = [];
        const canMutate = canMutateAdvertisement(role, ad.status);
        if (includeView) {
            items.push({
                id: "view",
                label: "Voir les détails",
                onClick: () => openDetail(ad)
            });
        }
        if (canMutate) {
            items.push({
                id: "edit",
                label: "Modifier",
                onClick: () => openEdit(ad)
            });
        }
        if (ad.status === "PENDING_APPROVAL") {
            if (canApprove) {
                items.push({
                    id: "approve",
                    label: "Approuver",
                    onClick: () => handleApprove(ad)
                });
                items.push({
                    id: "reject",
                    label: "Refuser",
                    onClick: () => handleHide(ad)
                });
            }
        } else if (ad.status !== "PUBLISHED") {
            if (canMutate) {
                items.push({
                    id: "publish",
                    label: isAdminPublisher ? "Publier" : "Demander la publication",
                    onClick: () => handlePublish(ad)
                });
            }
        } else if (canMutate) {
            items.push({
                id: "hide",
                label: "Masquer",
                onClick: () => handleHide(ad)
            });
        }
        if (canMutate) {
            items.push({
                id: "delete",
                label: "Supprimer",
                danger: true,
                onClick: () => setPendingDelete(ad)
            });
        }
        return items;
    };

    const deleteConfirmDialog = (
        <ConfirmDialog
            open={!!pendingDelete}
            title="Supprimer la publicité ?"
            message="La publicité ne sera plus visible. Cette action est définitive dans l'interface admin."
            confirmLabel="Supprimer"
            onConfirm={handleDelete}
            onCancel={() => setPendingDelete(null)}
        />
    );

    if (viewMode === "form") {
        return (
            <section className="card profile-section">
                <div className="section-header">
                    <div>
                        <BackLink
                            onClick={() => (selected ? setViewMode("detail") : backToList())}
                            className="purchase-detail-back"
                        >
                            {selected ? "Retour au détail" : "Retour à la liste"}
                        </BackLink>
                        <h2>{selected ? "Modifier la publicité" : "Nouvelle publicité"}</h2>
                    </div>
                </div>
                {!isAdminPublisher && (
                    <p className="muted advertisement-approval-hint">
                        La mise en ligne nécessite l&apos;approbation d&apos;un administrateur.
                        Votre demande passera en « En attente d&apos;approbation » et ne sera pas visible sur le catalogue immédiatement.
                    </p>
                )}
                <form className="profile-form" onSubmit={handleSave}>
                    <label className="field">
                        Code
                        <input
                            value={form.code}
                            onChange={(e) => setForm({ ...form, code: e.target.value })}
                            required
                            maxLength={64}
                            placeholder="partenaire-clim"
                        />
                    </label>
                    <label className="field">
                        Lien (site, Facebook, WhatsApp…)
                        <input
                            type="url"
                            value={form.url}
                            onChange={(e) => setForm({ ...form, url: e.target.value })}
                            required
                            placeholder="https://… ou lien WhatsApp / Facebook"
                        />
                    </label>
                    <label className="field">
                        Titre
                        <input
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            required
                            maxLength={255}
                        />
                    </label>
                    <label className="field">
                        Sous-titre
                        <input
                            value={form.subtitle}
                            onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                            maxLength={255}
                        />
                    </label>
                    <label className="field">
                        Description
                        <textarea
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            rows={3}
                            maxLength={2000}
                        />
                    </label>
                    <label className="field">
                        Insertion après N produits
                        <input
                            type="number"
                            min={0}
                            value={form.insertAfter}
                            onChange={(e) => setForm({ ...form, insertAfter: e.target.value })}
                        />
                    </label>
                    <label className="field">
                        Intervalle diaporama (secondes)
                        <input
                            type="number"
                            min={1}
                            step={1}
                            value={form.intervalSeconds}
                            onChange={(e) => setForm({ ...form, intervalSeconds: e.target.value })}
                        />
                    </label>
                    <label className="field">
                        Images
                        <input type="file" accept="image/*" multiple onChange={onUploadImages} />
                    </label>
                    <ImageUploadSaveHint visible={pendingImageSave} plural saveLabel="Enregistrer" />
                    {form.imageFileIds.length > 0 && (
                        <div className="admin-photo-gallery">
                            {form.imageFileIds.map((fileId, index) => (
                                <div key={fileId} className="admin-photo-item">
                                    <img src={`/api/v1/files/${fileId}`} alt={`Aperçu ${index + 1}`} />
                                    <div className="admin-photo-actions">
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-small"
                                            disabled={index === 0}
                                            onClick={() => moveImage(fileId, -1)}
                                        >
                                            ←
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-small"
                                            disabled={index === form.imageFileIds.length - 1}
                                            onClick={() => moveImage(fileId, 1)}
                                        >
                                            →
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-small"
                                            onClick={() => removeImage(fileId)}
                                        >
                                            Retirer
                                        </button>
                                    </div>
                                    {index === 0 && <span className="admin-photo-primary">Principale</span>}
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="profile-form-actions profile-form-actions--end">
                        <button type="submit" className="btn btn-primary btn-inline" disabled={saving}>
                            {saving ? "Enregistrement…" : "Enregistrer"}
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary btn-inline"
                            onClick={() => (selected ? setViewMode("detail") : backToList())}
                        >
                            Annuler
                        </button>
                    </div>
                </form>
                {deleteConfirmDialog}
            </section>
        );
    }

    if (viewMode === "detail" && selected) {
        return (
            <section className={`card profile-section advertisement-detail${selected.status === "PENDING_APPROVAL" ? " advertisement-pending" : ""}`}>
                <div className="section-header">
                    <div>
                        <BackLink onClick={backToList} className="purchase-detail-back">
                            Retour à la liste
                        </BackLink>
                        <h2>Détails de la publicité</h2>
                    </div>
                    <RowActionsMenu
                        ariaLabel={`Actions pour ${selected.title}`}
                        items={buildAdvertMenuItems(selected, { includeView: false })}
                    />
                </div>

                {selected.status === "PENDING_APPROVAL" && (
                    <p className={`advertisement-pending-banner${canApprove ? "" : " advertisement-pending-banner--staff"}`}>
                        {canApprove
                            ? "Cette publicité attend votre approbation avant d'être mise en ligne."
                            : "En attente d'approbation par un administrateur — pas encore visible sur le catalogue."}
                    </p>
                )}

                <ProductImageGallery
                    className="product-gallery--detail"
                    photoIds={selected.imageFileIds || []}
                    title={selected.title}
                    placeholder="Aucune image"
                />

                <dl className="profile-detail-view profile-detail-view--compact">
                    <div className="profile-detail-row">
                        <dt>Titre</dt>
                        <dd>{selected.title}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Statut</dt>
                        <dd>
                            <span className={advertisementStatusClassName(selected.status)}>
                                {formatAdvertisementStatus(selected.status)}
                            </span>
                        </dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Code</dt>
                        <dd>{selected.code}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Lien</dt>
                        <dd>
                            <a href={selected.url} target="_blank" rel="noopener noreferrer">
                                {selected.url}
                            </a>
                        </dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Sous-titre</dt>
                        <dd>{selected.subtitle || "—"}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Description</dt>
                        <dd>{selected.description || "—"}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Insertion</dt>
                        <dd>Après {selected.insertAfter} produit(s)</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Diaporama</dt>
                        <dd>{msToSeconds(selected.intervalMs)} s</dd>
                    </div>
                </dl>
                {deleteConfirmDialog}
            </section>
        );
    }

    return (
        <section className="card profile-section">
            <div className="section-header">
                <div>
                    <h2>Gérer les publicités</h2>
                    <p className="muted">
                        Cartes Publicité du catalogue (chargées en mémoire au démarrage).
                        {!isAdminPublisher
                            && " La publication nécessite l'approbation d'un administrateur. Une publicité déjà publiée est en consultation seule."}
                    </p>
                </div>
                <button type="button" className="btn btn-primary" onClick={openCreate}>
                    Nouvelle publicité
                </button>
            </div>
            {loading ? (
                <p className="muted">Chargement…</p>
            ) : ads.length === 0 ? (
                <p className="muted">Aucune publicité.</p>
            ) : (
                <div className="admin-table">
                    {ads.map((ad) => (
                        <div
                            key={ad.id}
                            className={`admin-row admin-list-row${ad.status === "PENDING_APPROVAL" ? " admin-list-row--pending-approval" : ""}`}
                        >
                            <button
                                type="button"
                                className="admin-list-row-main"
                                onClick={() => openDetail(ad)}
                            >
                                <div className="admin-row-body">
                                    <strong>{ad.title}</strong>
                                    <p className="muted">
                                        {ad.code}
                                        {" · "}
                                        <span className={advertisementStatusClassName(ad.status)}>
                                            {formatAdvertisementStatus(ad.status)}
                                        </span>
                                        {" · "}
                                        {ad.url}
                                    </p>
                                </div>
                                <span className="admin-list-row-chevron" aria-hidden="true">›</span>
                            </button>
                            <RowActionsMenu
                                ariaLabel={`Actions pour ${ad.title}`}
                                items={buildAdvertMenuItems(ad)}
                            />
                        </div>
                    ))}
                </div>
            )}
            {deleteConfirmDialog}
        </section>
    );
}
